/**
 * ProcedureInfoPage
 *
 * Full procedure information page styled in the app's dark theme.
 * Renders all structured data from the gm-content.json procedure card:
 *   - overview, eligibility (with expandable explainers), rights & limits,
 *     outcome, process steps, filings, and intake requirements.
 *
 * Used as the "step 0 / landing" screen inside ProcedureIntakePage.
 */

import React, { useState, useMemo } from 'react';
import { pickEn } from '../../procedures/procedureContent';
import './ProcedureInfoPage.css';

/* ── Helpers ──────────────────────────────────────────────────────── */

function p(obj) {
  return pickEn(obj) || '';
}

/** Map a filing's filer string to a badge label */
function filerBadge(filer = '') {
  if (!filer) return null;
  const map = {
    applicant: 'APPLICANT',
    employer: 'EMPLOYER',
    government: 'AUTHORITY',
    representative: 'REPRESENTATIVE',
  };
  return map[filer.toLowerCase()] || filer.toUpperCase();
}

/* ── Small presentational pieces ─────────────────────────────────── */

function SectionHeading({ label }) {
  return (
    <div className="pip-section-heading">
      <span className="pip-section-heading-text">{label}</span>
    </div>
  );
}

function SourceCite({ source }) {
  if (!source?.ref) return null;
  const el = (
    <span className="pip-cite">
      {p(source)}
      {source.ref}
    </span>
  );
  if (source.link) {
    return (
      <a
        href={source.link}
        target="_blank"
        rel="noopener noreferrer"
        className="pip-cite-link"
      >
        {source.ref}
      </a>
    );
  }
  return el;
}

/* ── Expandable explainer attached to an eligibility item ─────────  */

function Explainer({ explainer }) {
  const [open, setOpen] = useState(false);
  const question = p(explainer.question);
  const authoritative = explainer.authoritative || [];
  const nonAuth = explainer.non_authoritative || [];

  return (
    <div className="pip-explainer">
      <button
        type="button"
        className="pip-explainer-toggle"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="pip-explainer-minus">{open ? '−' : '+'}</span>
        {question}
      </button>
      {open && (
        <div className="pip-explainer-body">
          {authoritative.map((item, i) => (
            <div key={i} className="pip-explainer-block pip-explainer-block--auth">
              <div className="pip-explainer-block-label">From the regulation</div>
              <p className="pip-explainer-block-text">
                {p(item.point)}{' '}
                {item.source && <SourceCite source={item.source} />}
              </p>
            </div>
          ))}
          {nonAuth.map((item, i) => (
            <div key={i} className="pip-explainer-block pip-explainer-block--prac">
              <div className="pip-explainer-block-label">Practitioner guidance · not authoritative · verify</div>
              <p className="pip-explainer-block-text">
                {p(item.point)}{' '}
                {item.source && <SourceCite source={item.source} />}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Eligibility section ─────────────────────────────────────────── */

function EligibilitySection({ eligibility, explainers }) {
  if (!eligibility?.length) return null;

  const explainerMap = useMemo(() => {
    const m = {};
    (explainers || []).forEach((e) => {
      const key = e.applies_to;
      if (!m[key]) m[key] = [];
      m[key].push(e);
    });
    return m;
  }, [explainers]);

  return (
    <section className="pip-section">
      <SectionHeading label="Who can qualify" />
      <p className="pip-section-sub">
        You need to meet all of these.
      </p>
      <div className="pip-elig-list">
        {eligibility.map((item) => {
          const itemExplainers = explainerMap[item.id] || [];
          return (
            <div key={item.id} className="pip-elig-item">
              <div className="pip-elig-bullet" aria-hidden>■</div>
              <div className="pip-elig-content">
                <div className="pip-elig-label">{p(item.label)}</div>
                {item.detail && (
                  <div className="pip-elig-detail">{p(item.detail)}</div>
                )}
                {item.source?.ref && (
                  <div className="pip-elig-cite">
                    <SourceCite source={item.source} />
                  </div>
                )}
                {itemExplainers.map((exp) => (
                  <Explainer key={exp.id} explainer={exp} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ── Required documents (jurisdiction-schema procedures) ─────────── */

function RequiredDocumentsSection({ documents }) {
  if (!documents?.length) return null;
  return (
    <section className="pip-section">
      <SectionHeading label="Required documents" />
      <div className="pip-rl-list">
        {documents.map((doc) => (
          <div key={doc.id} className="pip-rl-item">
            <div className="pip-rl-bullet" aria-hidden>■</div>
            <div>
              <span className="pip-rl-text">{p(doc.text)}</span>
              <div className="pip-doc-provider">provided by: {doc.providedBy || 'applicant'}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── Rights & limits ─────────────────────────────────────────────── */

function RightsAndLimitsSection({ items }) {
  if (!items?.length) return null;
  return (
    <section className="pip-section">
      <SectionHeading label="What it lets you do, and its limits" />
      <div className="pip-rl-list">
        {items.map((item, i) => (
          <div key={item.id || i} className="pip-rl-item">
            <div className="pip-rl-bullet" aria-hidden>■</div>
            <div>
              <span className="pip-rl-text">{p(item.text)}</span>
              {item.source?.ref && (
                <span className="pip-rl-cite">
                  {' '}<SourceCite source={item.source} />
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── Outcome ─────────────────────────────────────────────────────── */

function OutcomeSection({ outcome }) {
  if (!outcome) return null;
  const bullets = [
    p(outcome.what_you_get),
    p(outcome.duration),
    p(outcome.takes_effect),
    p(outcome.renewal),
  ].filter(Boolean);

  return (
    <section className="pip-section">
      <SectionHeading label="What you get" />
      <div className="pip-rl-list">
        {bullets.map((text, i) => (
          <div key={i} className="pip-rl-item">
            <div className="pip-rl-bullet" aria-hidden>■</div>
            <span className="pip-rl-text">{text}</span>
          </div>
        ))}
        {outcome.source?.ref && (
          <div className="pip-cite-footer">
            <SourceCite source={outcome.source} />
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Process ─────────────────────────────────────────────────────── */

/** Groups consecutive steps that share a phase label into one section each. */
function groupStepsByPhase(steps) {
  const groups = [];
  (steps || []).forEach((step) => {
    const last = groups[groups.length - 1];
    if (last && last.label === step.phase) {
      last.steps.push(step);
    } else {
      groups.push({ label: step.phase || 'Step', steps: [step] });
    }
  });
  return groups;
}

function ProcessSection({ process, filings, forms, documents }) {
  if (!process?.steps?.length && !filings?.length) return null;

  const formMap = useMemo(() => {
    const m = {};
    (forms || []).forEach((f) => { m[f.id] = f; });
    return m;
  }, [forms]);

  const docMap = useMemo(() => {
    const m = {};
    (documents || []).forEach((d) => { m[d.id] = d; });
    return m;
  }, [documents]);

  const filingMap = useMemo(() => {
    const m = {};
    (filings || []).forEach((f) => { m[f.id] = f; });
    return m;
  }, [filings]);

  // Jurisdiction-schema procedures (``buildJurisdictionCard``) set ``showAllPhases`` and
  // give every step a distinct phase label already; older built cards only ever show the
  // "prepare" phase (other phases were never modeled for those cards).
  const allSteps = process.steps || [];
  const phaseGroups = process.showAllPhases
    ? groupStepsByPhase(allSteps)
    : [{ label: 'Prepare', steps: allSteps.filter((s) => s.phase === 'prepare') }];

  // Group filings by order
  const orderedFilings = [...(filings || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <section className="pip-section">
      <SectionHeading label="The process" />

      {phaseGroups.map((group, gi) => group.steps.length > 0 && (
        <div key={group.label + gi} className="pip-process-phase">
          <div className="pip-process-phase-label">{group.label}</div>
          {group.steps.map((step, i) => (
            <div key={i} className="pip-process-step">
              {step.acts && (
                <span className="pip-actor-badge">{filerBadge(step.acts)}</span>
              )}
              <div>
                <span className="pip-process-step-text">{p(step.what)}</span>
                {step.deadline && (
                  <span className="pip-deadline-badge">{step.deadline}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}

      {orderedFilings.length > 0 && (
        <div className="pip-process-phase">
          <div className="pip-process-phase-label">The filings (submissions in order)</div>
          {orderedFilings.map((filing) => {
            const filingForms = (filing.contains_forms || []).map((fid) => formMap[fid]).filter(Boolean);
            const filingDocs = (filing.contains_documents || []).map((did) => docMap[did]).filter(Boolean);
            return (
              <div key={filing.id} className="pip-filing">
                <div className="pip-filing-header">
                  <span className="pip-filing-order">{filing.order}</span>
                  <div>
                    <div className="pip-filing-title">{p(filing.label)}</div>
                    <div className="pip-filing-meta">
                      filed by{' '}
                      <strong>{filing.filer}</strong>
                      {filing.office && (
                        <> · at {p(filing.office)}</>
                      )}
                    </div>
                  </div>
                </div>

                {filingForms.length > 0 && (
                  <div className="pip-filing-sub">
                    <div className="pip-filing-sub-label">Forms</div>
                    {filingForms.map((form) => (
                      <div key={form.id} className="pip-filing-sub-item">
                        <span className="pip-sub-bullet">•</span>
                        <div>
                          <span className="pip-form-name">{form.name}</span>
                          {form.title && (
                            <span className="pip-form-title">: {p(form.title)}</span>
                          )}
                          <div className="pip-filing-sub-meta">
                            filled by: {form.filled_by}
                            {form.where_to_get && (
                              <>
                                {' · '}
                                <a
                                  href={form.where_to_get}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="pip-form-link"
                                >
                                  form link
                                </a>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {filingDocs.length > 0 && (
                  <div className="pip-filing-sub">
                    <div className="pip-filing-sub-label">Documents</div>
                    {filingDocs.map((doc) => (
                      <div key={doc.id} className="pip-filing-sub-item">
                        <span className="pip-sub-bullet">•</span>
                        <div>
                          <div className="pip-doc-desc">{p(doc.human_description)}</div>
                          <div className="pip-filing-sub-meta">provided by: {doc.provided_by}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ── What you'll need to provide ────────────────────────────────── */

function IntakeRequirementsSection({ intakeItems }) {
  if (!intakeItems?.length) return null;

  const applicantInfo = intakeItems.filter((i) => i.source === 'applicant' && i.kind === 'info');
  const applicantDocs = intakeItems.filter((i) => i.source === 'applicant' && i.kind === 'document');
  const employerItems = intakeItems.filter((i) => i.source === 'employer');
  const authorityItems = intakeItems.filter((i) => i.source === 'authority' || i.source === 'government');

  return (
    <section className="pip-section pip-section--last">
      <SectionHeading label="What you'll need to provide" />

      {(applicantInfo.length > 0 || applicantDocs.length > 0) && (
        <div className="pip-intake-group">
          <div className="pip-intake-group-title">From you (the applicant)</div>
          <div className="pip-intake-group-sub">
            the personal facts and documents the system collects from you
          </div>

          {applicantInfo.length > 0 && (
            <>
              <div className="pip-intake-kind-label">Information</div>
              <ul className="pip-intake-list">
                {applicantInfo.map((item) => (
                  <li key={item.id} className="pip-intake-item">
                    <span className="pip-intake-bullet">■</span>
                    {p(item.prompt?.text)}
                  </li>
                ))}
              </ul>
            </>
          )}

          {applicantDocs.length > 0 && (
            <>
              <div className="pip-intake-kind-label">Documents</div>
              <ul className="pip-intake-list">
                {applicantDocs.map((item) => (
                  <li key={item.id} className="pip-intake-item">
                    <span className="pip-intake-bullet">■</span>
                    {p(item.prompt?.text)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {employerItems.length > 0 && (
        <div className="pip-intake-group">
          <div className="pip-intake-group-title">From the employer</div>
          <ul className="pip-intake-list">
            {employerItems.map((item) => (
              <li key={item.id} className="pip-intake-item">
                <span className="pip-intake-bullet">■</span>
                {p(item.prompt?.text)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {authorityItems.length > 0 && (
        <div className="pip-intake-group">
          <div className="pip-intake-group-title">Checked by the authorities</div>
          <div className="pip-intake-group-sub">
            verified by the administration; nothing for anyone to submit
          </div>
          <ul className="pip-intake-list">
            {authorityItems.map((item) => (
              <li key={item.id} className="pip-intake-item">
                <span className="pip-intake-bullet">■</span>
                {p(item.prompt?.text)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Main export
   ════════════════════════════════════════════════════════════════════ */

export default function ProcedureInfoPage({ procedure, bundle, intakeItems, onStart, participantGreeting }) {
  const card = bundle?.card || {};

  const title         = p(procedure?.title);
  const subtitle      = p(procedure?.subtitle);
  const jurisdiction  = p(procedure?.jurisdiction);
  const overview      = p(procedure?.overview);
  const distinguishFrom = p(procedure?.distinguish_from);

  return (
    <div className="pip-page">

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <div className="pip-hero">
        <div className="pip-hero-inner">

          {/* Breadcrumb */}
          <div className="pip-hero-eyebrow">
            <span className="pip-live-dot" />
            Global Mobility · {jurisdiction}
          </div>

          {/* Title */}
          {participantGreeting?.firstName ? (
            <p className="pip-hero-greeting text-sm text-emerald-200/90 mb-2">
              Hi {participantGreeting.firstName}
              {participantGreeting.projectName ? ` - ${participantGreeting.projectName}` : ''}
            </p>
          ) : null}
          <h1 className="pip-hero-title">{title}</h1>
          {subtitle && <p className="pip-hero-subtitle">{subtitle}</p>}

          {/* CTA */}
          <button type="button" className="pip-cta" onClick={onStart}>
            Start application →
          </button>

          {/* Overview */}
          {overview && (
            <div className="pip-overview">
              <p>{overview}</p>
            </div>
          )}

          {/* Distinguish from */}
          {distinguishFrom && (
            <div className="pip-distinguish">
              <span className="pip-distinguish-label">Not the same as: </span>
              {distinguishFrom}
            </div>
          )}
        </div>
      </div>

      {/* ── Content ──────────────────────────────────────────────── */}
      <div className="pip-content">

        <EligibilitySection
          eligibility={card.eligibility}
          explainers={card.explainers}
        />

        <RequiredDocumentsSection documents={card.required_documents} />

        <RightsAndLimitsSection items={card.rights_and_limits} />

        <OutcomeSection outcome={card.outcome} />

        <ProcessSection
          process={card.process}
          filings={card.filings}
          forms={card.forms}
          documents={card.documents}
        />

        <IntakeRequirementsSection intakeItems={intakeItems} />

        {/* Bottom CTA */}
        <div className="pip-bottom-cta">
          <button type="button" className="pip-cta" onClick={onStart}>
            Start application →
          </button>
        </div>

      </div>
    </div>
  );
}
